import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Vehicle } from '@vehicle-logbook/shared';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Edit, Gauge, Car } from 'lucide-react';
import { Modal, ModalBody, ModalFooter } from '../common/Modal';
import { btnPrimary, btnSecondary, inputClass } from '../common/formStyles';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

interface VehicleDetailsProps {
  vehicle: Vehicle;
  onClose: () => void;
  onEdit: () => void;
}

export const VehicleDetails = ({
  vehicle,
  onClose,
  onEdit,
}: VehicleDetailsProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isMileageEditOpen, setIsMileageEditOpen] = useState(false);
  const [newMileage, setNewMileage] = useState(
    vehicle.currentMileage.toString()
  );

  const updateMileageMutation = useMutation({
    mutationFn: async (mileage: number) => {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();
      const response = await axios.patch(
        `${API_URL}/vehicles/${vehicle.id}/mileage`,
        { mileage },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success(t('vehicle.mileageUpdated'));
      setIsMileageEditOpen(false);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || t('errors.generic');
      toast.error(errorMessage);
    },
  });

  const handleMileageUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    const mileage = parseFloat(newMileage);
    if (isNaN(mileage) || mileage < vehicle.currentMileage) {
      toast.error(t('vehicle.mileageError'));
      return;
    }
    await updateMileageMutation.mutateAsync(mileage);
  };

  const getVehicleTypeLabel = (type: string) => {
    return t(`vehicle.types.${type}`);
  };

  return (
    <Modal title={vehicle.name} onClose={onClose}>
      <ModalBody>
        {vehicle.photoUrl ? (
          <div className="mx-auto mb-4 w-full max-w-md">
            <img
              src={vehicle.photoUrl}
              alt={vehicle.name}
              className="max-h-48 w-full rounded-lg object-contain"
            />
          </div>
        ) : (
          <div className="mx-auto mb-4 flex aspect-video max-w-xs items-center justify-center rounded-lg bg-gray-100">
            <Car className="h-12 w-12 text-gray-400" />
          </div>
        )}

        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="mb-0.5 block text-xs font-medium text-gray-500">
              {t('vehicle.make')}
            </label>
            <p className="text-sm text-gray-900">{vehicle.make}</p>
          </div>

          <div>
            <label className="mb-0.5 block text-xs font-medium text-gray-500">
              {t('vehicle.model')}
            </label>
            <p className="text-sm text-gray-900">{vehicle.model}</p>
          </div>

          <div>
            <label className="mb-0.5 block text-xs font-medium text-gray-500">
              {t('vehicle.year')}
            </label>
            <p className="text-sm text-gray-900">{vehicle.year}</p>
          </div>

          <div>
            <label className="mb-0.5 block text-xs font-medium text-gray-500">
              {t('vehicle.type')}
            </label>
            <p className="text-sm text-gray-900">
              {getVehicleTypeLabel(vehicle.type)}
            </p>
          </div>

          {vehicle.vin && (
            <div>
              <label className="mb-0.5 block text-xs font-medium text-gray-500">
                {t('vehicle.vin')}
              </label>
              <p className="font-mono text-sm text-gray-900">{vehicle.vin}</p>
            </div>
          )}

          {vehicle.licensePlate && (
            <div>
              <label className="mb-0.5 block text-xs font-medium text-gray-500">
                {t('vehicle.licensePlate')}
              </label>
              <p className="text-sm text-gray-900">{vehicle.licensePlate}</p>
            </div>
          )}

          <div className="md:col-span-2">
            <div className="flex items-center justify-between">
              <div>
                <label className="mb-0.5 block text-xs font-medium text-gray-500">
                  {t('vehicle.currentMileage')}
                </label>
                <p className="flex items-center text-lg font-bold text-gray-900">
                  <Gauge className="mr-2 h-5 w-5" />
                  {vehicle.currentMileage.toLocaleString()} {t('vehicle.km')}
                </p>
              </div>
              {!isMileageEditOpen && (
                <button
                  onClick={() => setIsMileageEditOpen(true)}
                  className={btnSecondary}
                >
                  {t('vehicle.updateMileage')}
                </button>
              )}
            </div>

            {isMileageEditOpen && (
              <form
                onSubmit={handleMileageUpdate}
                className="mt-3 flex items-center gap-2"
              >
                <input
                  type="number"
                  min={vehicle.currentMileage}
                  step="1"
                  value={newMileage}
                  onChange={(e) => setNewMileage(e.target.value)}
                  className={inputClass}
                  required
                />
                <button
                  type="submit"
                  disabled={updateMileageMutation.isPending}
                  className={btnPrimary}
                >
                  {updateMileageMutation.isPending
                    ? t('common.loading')
                    : t('common.save')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsMileageEditOpen(false);
                    setNewMileage(vehicle.currentMileage.toString());
                  }}
                  className={btnSecondary}
                >
                  {t('common.cancel')}
                </button>
              </form>
            )}
          </div>
        </div>

        {vehicle.specifications &&
          Object.keys(vehicle.specifications).length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                {t('vehicle.specifications')}
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(vehicle.specifications).map(([key, value]) => (
                  <div key={key}>
                    <label className="mb-0.5 block text-xs font-medium text-gray-500">
                      {key}
                    </label>
                    <p className="text-sm text-gray-900">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
      </ModalBody>

      <ModalFooter>
        <button onClick={onClose} className={btnSecondary}>
          {t('common.close')}
        </button>
        <button
          onClick={onEdit}
          className={`${btnPrimary} inline-flex items-center`}
        >
          <Edit className="mr-1.5 h-4 w-4" />
          {t('common.edit')}
        </button>
      </ModalFooter>
    </Modal>
  );
};
